
import { useState, useCallback, useRef, useEffect } from 'react';
import { EmailTemplate } from '@/types/emailTemplates';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { toast } from 'sonner';
import { getCachedTemplate, setCachedTemplate } from './useTemplateCache';
import { supabase } from '@/integrations/supabase/client';

export function useTemplateLoading(templateId: string) {
  const { fetchTemplate } = useEmailTemplates();
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [authReady, setAuthReady] = useState(false);
  
  // Use refs to manage state without triggering re-renders
  const loadingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const backoffMultiplierRef = useRef(1);
  const templateIdRef = useRef<string>(templateId);
  const prevTemplateIdRef = useRef<string | null>(null);
  
  // Track if the component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // Update templateIdRef when templateId changes to avoid stale closures
  useEffect(() => {
    templateIdRef.current = templateId;
  }, [templateId]);
  
  // First check if we're authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          console.log('User is authenticated, ready to load templates');
          setAuthReady(true);
        } else {
          console.error('No auth session found');
          setError(new Error('Authentication required'));
          setAuthReady(false);
        }
      } catch (err) {
        console.error('Error checking auth status:', err);
        setError(new Error('Authentication error'));
      }
    };

    checkAuth();
  }, []);
  
  // Cleanup function to prevent memory leaks and updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      loadingRef.current = false;
    };
  }, []);
  
  // Optimized template loading with caching and dependency stability
  const loadTemplate = useCallback(async () => {
    const currentTemplateId = templateIdRef.current;
    
    // Skip if no template ID
    if (!currentTemplateId) {
      console.log('No template ID provided, skipping load');
      if (isMountedRef.current) {
        setLoading(false);
      }
      return;
    }
    
    // Skip if auth is not ready
    if (!authReady) {
      console.log('Auth not ready, skipping template load');
      return;
    }
    
    // Skip if already loading
    if (loadingRef.current) {
      console.log(`Already loading template ${currentTemplateId}, skipping.`);
      return;
    }
    
    // Skip if template ID hasn't changed and template is already loaded
    if (prevTemplateIdRef.current === currentTemplateId && template !== null) {
      console.log(`Template ID ${currentTemplateId} already loaded, skipping.`);
      return;
    }
    
    // Only set loading state for the initial load
    if (template === null || prevTemplateIdRef.current !== currentTemplateId) {
      console.log(`Loading template with ID: ${currentTemplateId}`);
      prevTemplateIdRef.current = currentTemplateId;
      
      // Prevent concurrent loads
      loadingRef.current = true;
      
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }
      
      // Check cache first
      const cachedTemplate = getCachedTemplate(currentTemplateId);
      
      if (cachedTemplate) {
        console.log(`Using cached template for ID: ${currentTemplateId}`, cachedTemplate);
        
        if (isMountedRef.current) {
          setTemplate(cachedTemplate);
          
          // Reset retry counter on success
          retryCountRef.current = 0;
          backoffMultiplierRef.current = 1;
          setLoading(false);
        }
        loadingRef.current = false;
        return;
      }
      
      // Set up abort controller to cancel pending requests if component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      try {
        console.log(`Fetching template with ID: ${currentTemplateId} (attempt ${retryCountRef.current + 1})`);
        const templateData = await fetchTemplate(currentTemplateId);
        
        if (templateData) {
          console.log("Template data loaded successfully:", templateData.name);
          
          // Cache the result
          setCachedTemplate(currentTemplateId, templateData);
          
          if (isMountedRef.current) {
            setTemplate(templateData);
            
            // Reset retry counter on success
            retryCountRef.current = 0;
            backoffMultiplierRef.current = 1;
            setLoading(false);
          }
        } else {
          throw new Error(`Failed to fetch template data for ID: ${currentTemplateId}`);
        }
      } catch (err: any) {
        console.error('Error loading template:', err);
        
        if (isMountedRef.current) {
          setError(err);
          
          // Check for cached template as fallback even before all retries are exhausted
          const cachedData = getCachedTemplate(currentTemplateId);
          if (cachedData) {
            console.log("Using cached template data as fallback after error");
            setTemplate(cachedData);
            toast.info("Using cached template data");
            setLoading(false);
            loadingRef.current = false;
            return;
          }
          
          // Implement exponential backoff only for specific error types
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current++;
            // Exponential backoff with increasing multiplier to spread retries further apart
            const backoffTime = Math.min(1000 * Math.pow(1.5, retryCountRef.current) * backoffMultiplierRef.current, 10000);
            backoffMultiplierRef.current += 0.5; // Increase multiplier for each retry
            
            console.log(`Will retry in ${backoffTime}ms (attempt ${retryCountRef.current}/${maxRetries})`);
            setTimeout(() => {
              if (isMountedRef.current) {
                loadingRef.current = false;
                loadTemplate();
              }
            }, backoffTime);
          } else {
            console.error(`Failed to load template after ${maxRetries} attempts`);
            toast.error(`Failed to load template after ${maxRetries} attempts`);
            setLoading(false);
            loadingRef.current = false;
          }
        }
      } finally {
        if (!retryCountRef.current || retryCountRef.current >= maxRetries) {
          loadingRef.current = false;
        }
      }
    }
  }, [fetchTemplate, template, authReady]);
  
  // Auto-load template when templateId changes and auth is ready
  useEffect(() => {
    if (authReady && templateId) {
      loadTemplate();
    }
  }, [templateId, authReady, loadTemplate]);
  
  return {
    loading,
    template,
    error,
    loadTemplate,
    isMountedRef,
    authReady
  };
}
